// Avatar builder (beta) parts catalog.
//
// Everything a built avatar is made of: two CC0 VRoid base bodies, skin
// tones, procedural hairstyles (shapes in hairGeometry.ts) with Meta Muse
// strand textures, hair colours, Muse iris art and Muse fabric prints.
// Diversity is a requirement, not an afterthought: lists lead with deep and
// brown skin, textured hair and dark brown eyes.

import { normalizeExtras, type BuilderExtras } from './extras';

export const BUILDER_ASSETS = 'https://assets.gathrxr.com/avatars/builder/v1/';
export const builderAsset = (path: string) => BUILDER_ASSETS + path;

export type BaseId = 'a' | 'b';

export interface BaseDef {
  id: BaseId;
  name: string;
  hint: string;
  file: string;
  /** Garment slots this base has. */
  garments: ('tops' | 'bottoms' | 'shoes')[];
}

export const BASES: BaseDef[] = [
  { id: 'a', name: 'Body A', hint: 'Long-sleeve dress silhouette', file: 'bases/base-f.vrm', garments: ['tops', 'shoes'] },
  { id: 'b', name: 'Body B', hint: 'Hoodie + trousers silhouette', file: 'bases/base-m.vrm', garments: ['tops', 'bottoms', 'shoes'] },
];

export interface SkinTone { id: string; name: string; hex: string; /** non-human tone (fantasy characters) */ fantasy?: boolean }
/** Deep tones first, the full human range, then fantasy tones. `hex` is the lit mid-tone the base skin is moved to. */
export const SKIN_TONES: SkinTone[] = [
  { id: 'ebony', name: 'Ebony', hex: '#3d2418' },
  { id: 'espresso', name: 'Espresso', hex: '#4e2e1f' },
  { id: 'mahogany', name: 'Mahogany', hex: '#58301f' },
  { id: 'deep-brown', name: 'Deep brown', hex: '#603b27' },
  { id: 'rich-brown', name: 'Rich brown', hex: '#74472f' },
  { id: 'chestnut', name: 'Chestnut', hex: '#8a5739' },
  { id: 'bronze', name: 'Bronze', hex: '#94603a' },
  { id: 'warm-brown', name: 'Warm brown', hex: '#a06a45' },
  { id: 'deep-olive', name: 'Deep olive', hex: '#9c7a52' },
  { id: 'caramel', name: 'Caramel', hex: '#b67f52' },
  { id: 'honey', name: 'Honey', hex: '#c9955f' },
  { id: 'olive', name: 'Olive', hex: '#c09a72' },
  { id: 'tan', name: 'Warm tan', hex: '#cf9f74' },
  { id: 'golden', name: 'Golden tan', hex: '#d8ab7e' },
  { id: 'light-olive', name: 'Light olive', hex: '#d9bb92' },
  { id: 'warm-beige', name: 'Warm beige', hex: '#e2b993' },
  { id: 'beige', name: 'Light beige', hex: '#e9c5a3' },
  { id: 'peach', name: 'Peach', hex: '#f2cbb0' },
  { id: 'fair', name: 'Fair', hex: '#f3d8c3' },
  { id: 'cool-fair', name: 'Cool fair', hex: '#f0d4cc' },
  { id: 'porcelain', name: 'Porcelain', hex: '#f7e5da' },
  // fantasy tones
  { id: 'fx-azure', name: 'Azure', hex: '#5f8fd6', fantasy: true },
  { id: 'fx-jade', name: 'Jade', hex: '#5fae7e', fantasy: true },
  { id: 'fx-lavender', name: 'Lavender', hex: '#b6a0e6', fantasy: true },
  { id: 'fx-silver', name: 'Silver', hex: '#c3c8d2', fantasy: true },
  { id: 'fx-ember', name: 'Ember', hex: '#c8583a', fantasy: true },
  { id: 'fx-night', name: 'Midnight', hex: '#3b3f74', fantasy: true },
];
export const HUMAN_SKIN_TONES = SKIN_TONES.filter((s) => !s.fantasy);

export type HairTexture = 'coil' | 'locs' | 'braid' | 'straight' | 'curl' | 'waves' | 'buzz' | 'fabric' | 'wavy' | 'loose';
export const HAIR_TEXTURES: Record<HairTexture, string> = {
  coil: 'tex/coil.jpg', locs: 'tex/locs.jpg', braid: 'tex/braid.jpg', straight: 'tex/straight.jpg',
  curl: 'tex/curl.jpg', waves: 'tex/waves.jpg', buzz: 'tex/buzz.jpg', fabric: 'tex/fabric.jpg',
  wavy: 'hair2/tex/wavy.jpg', loose: 'hair2/tex/loose.jpg',
};

export interface HairStyle {
  id: string;
  name: string;
  /** Words the AI picker matches on. */
  tags: string;
  /** Head coverings take fabric colours/prints instead of hair colours. */
  fabric?: boolean;
  thumb: string;
  /** Picker thumbnail folder (hair2 = styles added later; existing files are never overwritten). */
  v2?: boolean;
}

/** Textured / Black hairstyles lead the list; styles from every background follow. */
export const HAIR_STYLES: HairStyle[] = [
  { id: 'afro', name: 'Afro', tags: 'big round natural afro, 4c coils, volume' },
  { id: 'twa', name: 'TWA / short coils', tags: 'teeny weeny afro, short natural coils, big chop' },
  { id: 'locs', name: 'Locs', tags: 'dreadlocks, locs, shoulder length, sisterlocks' },
  { id: 'box-braids', name: 'Box braids', tags: 'long box braids, knotless braids, protective style' },
  { id: 'cornrows', name: 'Cornrows', tags: 'straight-back cornrows, feed-in braids, braided rows' },
  { id: 'bantu', name: 'Bantu knots', tags: 'bantu knots, zulu knots, twisted knots' },
  { id: 'puffs', name: 'Afro puffs', tags: 'two afro puffs, pigtail puffs, space buns natural' },
  { id: 'waves', name: '360 waves', tags: 'waves, 360 waves, short brushed waves, line up' },
  { id: 'fade-twists', name: 'Fade + twists', tags: 'high fade, short twists on top, sponge twists, high top' },
  { id: 'curls', name: 'Defined curls', tags: 'shoulder-length spiral curls, 3b 3c curly hair, ringlets' },
  { id: 'curly-fro', name: 'Curly fro', tags: 'rounded curly fro, 3c curls, short-medium curly volume', v2: true },
  { id: 'mohawk', name: 'Mohawk', tags: 'spiky mohawk, shaved sides, frohawk, punk', v2: true },
  { id: 'loose-curls', name: 'Loose curls', tags: 'loose bouncy curls, 3a 3b curls, shoulder length curly hair', v2: true },
  { id: 'silk-press', name: 'Silk press', tags: 'long sleek straight silk press, middle part, blowout' },
  { id: 'bob', name: 'Bob with bangs', tags: 'straight chin-length bob, bangs, fringe' },
  { id: 'long-straight', name: 'Long straight', tags: 'very long straight hair, middle part, sleek' },
  { id: 'hime', name: 'Hime cut', tags: 'hime cut, long straight hair with blunt bangs / fringe and cheek-length side locks', v2: true },
  { id: 'beach-waves', name: 'Beach waves', tags: 'long wavy hair, beach waves, loose waves, 2a 2b, side part', v2: true },
  { id: 'long-braid', name: 'Long braid', tags: 'single long braid down the back, plait, long straight hair braided', v2: true },
  { id: 'braid-crown', name: 'Braided crown', tags: 'crown braid, halo braid, braids wrapped around the head, milkmaid braid', v2: true },
  { id: 'ponytail', name: 'High ponytail', tags: 'high ponytail, sleek pony' },
  { id: 'bun', name: 'Top bun', tags: 'high bun, updo, ballerina bun' },
  { id: 'man-bun', name: 'Top knot', tags: 'man bun, top knot, samurai knot, long hair tied up at the back of the crown', v2: true },
  { id: 'short-crop', name: 'Short crop', tags: 'short straight textured crop, fringe, short hair' },
  { id: 'side-part', name: 'Side part', tags: 'short side part, classic gentleman cut, combed over, comb over', v2: true },
  { id: 'slick-back', name: 'Slick back', tags: 'slicked back hair, combed straight back, glossy', v2: true },
  { id: 'undercut', name: 'Undercut', tags: 'undercut, shaved sides, longer top swept back / to the side', v2: true },
  { id: 'mullet', name: 'Mullet', tags: 'mullet, short on top and sides, long at the back, wolf cut', v2: true },
  { id: 'buzz', name: 'Buzz cut', tags: 'buzz cut, very short, shaved' },
  { id: 'bald', name: 'Bald', tags: 'bald, shaved head, no hair' },
  { id: 'hijab', name: 'Hijab', tags: 'hijab, head scarf, covered hair, modest', fabric: true },
  { id: 'headwrap', name: 'Headwrap', tags: 'headwrap, gele, turban, head tie, wrapped scarf', fabric: true },
].map((h: Omit<HairStyle, 'thumb'>) => ({ ...h, thumb: `${h.v2 ? 'hair2/thumbs' : 'thumbs'}/hair-${h.id}.webp` }));

export interface HairColor { id: string; name: string; hex: string; /** dyed / fantasy colour (not a natural shade) */ bold?: boolean }
export const HAIR_COLORS: HairColor[] = [
  { id: 'jet-black', name: 'Jet black', hex: '#2c2629' },
  { id: 'soft-black', name: 'Soft black', hex: '#3b302b' },
  { id: 'dark-brown', name: 'Dark brown', hex: '#47301f' },
  { id: 'ash-brown', name: 'Ash brown', hex: '#5f5146' },
  { id: 'chestnut', name: 'Chestnut', hex: '#6e4429' },
  { id: 'light-brown', name: 'Light brown', hex: '#8a6646' },
  { id: 'auburn', name: 'Auburn', hex: '#8e3d24' },
  { id: 'ginger', name: 'Ginger red', hex: '#c0542a' },
  { id: 'copper', name: 'Copper', hex: '#b65d2e' },
  { id: 'strawberry', name: 'Strawberry blonde', hex: '#d68f62' },
  { id: 'honey-blonde', name: 'Honey blonde', hex: '#c99a5b' },
  { id: 'golden-blonde', name: 'Golden blonde', hex: '#ddb46a' },
  { id: 'ash-blonde', name: 'Ash blonde', hex: '#c9bc9c' },
  { id: 'platinum', name: 'Platinum', hex: '#e7dcc4' },
  { id: 'salt-pepper', name: 'Salt & pepper', hex: '#7c7a7a' },
  { id: 'silver', name: 'Silver grey', hex: '#b8b8c0' },
  { id: 'white', name: 'White', hex: '#ecebe8' },
  { id: 'burgundy', name: 'Burgundy', hex: '#6f2034', bold: true },
  { id: 'rose', name: 'Rose pink', hex: '#e48fb0', bold: true },
  { id: 'electric-blue', name: 'Electric blue', hex: '#3168e0', bold: true },
  { id: 'lavender', name: 'Lavender', hex: '#a68ae0', bold: true },
  { id: 'emerald', name: 'Emerald', hex: '#1f8a5b', bold: true },
  { id: 'flame', name: 'Flame orange', hex: '#ea6a1f', bold: true },
  { id: 'teal', name: 'Teal', hex: '#1d9a9e', bold: true },
];

export interface FabricColor { id: string; name: string; hex: string; pattern?: string; scale?: number }
/** Hijab / headwrap fabrics: solids plus Muse prints. */
export const FABRIC_COLORS: FabricColor[] = [
  { id: 'f-black', name: 'Black', hex: '#232023' },
  { id: 'f-cream', name: 'Cream', hex: '#efe6d6' },
  { id: 'f-sand', name: 'Sand', hex: '#c8ae8a' },
  { id: 'f-terracotta', name: 'Terracotta', hex: '#b8573a' },
  { id: 'f-emerald', name: 'Emerald', hex: '#1d7a55' },
  { id: 'f-royal', name: 'Royal blue', hex: '#2c4aa8' },
  { id: 'f-plum', name: 'Plum', hex: '#6b2f63' },
  { id: 'f-mustard', name: 'Mustard', hex: '#d4a020' },
  { id: 'f-blush', name: 'Blush', hex: '#e7a9a6' },
  { id: 'f-ankara-sun', name: 'Ankara sun print', hex: '#ffffff', pattern: 'ankara-sun', scale: 2 },
  { id: 'f-ankara-bloom', name: 'Ankara bloom print', hex: '#ffffff', pattern: 'ankara-bloom', scale: 2 },
  { id: 'f-kente', name: 'Kente-inspired', hex: '#ffffff', pattern: 'kente', scale: 2 },
  { id: 'f-adire', name: 'Adire-inspired indigo', hex: '#ffffff', pattern: 'adire', scale: 2 },
];

export interface EyeColor { id: string; name: string; swatch: string; fantasy?: boolean }
export const EYE_COLORS: EyeColor[] = [
  { id: 'dark-brown', name: 'Dark brown', swatch: '#2b1a12' },
  { id: 'brown', name: 'Brown', swatch: '#5a3420' },
  { id: 'amber', name: 'Amber', swatch: '#a8691e' },
  { id: 'hazel', name: 'Hazel', swatch: '#7d6a2c' },
  { id: 'green', name: 'Green', swatch: '#3f7d3a' },
  { id: 'blue', name: 'Blue', swatch: '#3a6fc4' },
  { id: 'grey', name: 'Grey', swatch: '#8a9098' },
  { id: 'violet', name: 'Violet', swatch: '#7a4bc4', fantasy: true },
  { id: 'gold', name: 'Gold', swatch: '#d6a62a', fantasy: true },
  { id: 'crimson', name: 'Crimson', swatch: '#b0202c', fantasy: true },
  { id: 'teal', name: 'Teal', swatch: '#1ba6a6', fantasy: true },
  { id: 'rose', name: 'Rose', swatch: '#e27aa3', fantasy: true },
];
export const irisAsset = (id: string) => `iris/${id}.png`;

export const PATTERNS = ['kente', 'ankara-sun', 'ankara-bloom', 'adire', 'mudcloth', 'afrofuture', 'neon', 'camo', 'denim', 'tiedye', 'boho', 'pinstripe', 'batik', 'knit'] as const;
export type PatternId = (typeof PATTERNS)[number];
export const patternAsset = (id: string) => `pat/${id}.jpg`;

export interface Garment { color: string; pattern?: PatternId; scale?: number; shading?: number }
export interface Outfit {
  id: string;
  base: BaseId;
  name: string;
  vibe: 'streetwear' | 'afrofuturist' | 'business' | 'casual' | 'festival' | 'cyberpunk' | 'traditional-inspired';
  tags: string;
  tops: Garment;
  bottoms?: Garment;
  shoes: Garment;
  /** Pattern shown on the picker card (else a colour chip). */
  thumbPattern?: PatternId;
}

export const OUTFITS: Outfit[] = [
  // ---- Body A: maxi dress ----
  { id: 'a-ankara-sun', base: 'a', name: 'Ankara sun dress', vibe: 'traditional-inspired', tags: 'African wax print inspired dress, bold, colourful', tops: { color: '#ffffff', pattern: 'ankara-sun', scale: 5 }, shoes: { color: '#3a2618' }, thumbPattern: 'ankara-sun' },
  { id: 'a-kente', base: 'a', name: 'Kente-inspired gown', vibe: 'traditional-inspired', tags: 'kente inspired strip cloth gown, gold green red', tops: { color: '#ffffff', pattern: 'kente', scale: 5 }, shoes: { color: '#c9a227' }, thumbPattern: 'kente' },
  { id: 'a-afrofuture', base: 'a', name: 'Afrofuturist gold', vibe: 'afrofuturist', tags: 'afrofuturist, gold geometry, purple, regal sci-fi', tops: { color: '#ffffff', pattern: 'afrofuture', scale: 4 }, shoes: { color: '#d4af37' }, thumbPattern: 'afrofuture' },
  { id: 'a-adire', base: 'a', name: 'Adire indigo dress', vibe: 'traditional-inspired', tags: 'indigo resist dye inspired, blue and white', tops: { color: '#ffffff', pattern: 'adire', scale: 5 }, shoes: { color: '#efe6d6' }, thumbPattern: 'adire' },
  { id: 'a-business', base: 'a', name: 'Navy business dress', vibe: 'business', tags: 'professional, office, navy, smart', tops: { color: '#24304f', shading: 1.1 }, shoes: { color: '#1a1a1c' } },
  { id: 'a-charcoal', base: 'a', name: 'Pinstripe sheath', vibe: 'business', tags: 'professional, pinstripe, charcoal, formal', tops: { color: '#ffffff', pattern: 'pinstripe', scale: 6 }, shoes: { color: '#1a1a1c' }, thumbPattern: 'pinstripe' },
  { id: 'a-denim', base: 'a', name: 'Denim day dress', vibe: 'casual', tags: 'casual, denim, everyday', tops: { color: '#ffffff', pattern: 'denim', scale: 6 }, shoes: { color: '#f4f4f2' }, thumbPattern: 'denim' },
  { id: 'a-blush', base: 'a', name: 'Blush casual', vibe: 'casual', tags: 'casual, soft pink, simple', tops: { color: '#e9a9ae' }, shoes: { color: '#f4f4f2' } },
  { id: 'a-boho', base: 'a', name: 'Festival boho', vibe: 'festival', tags: 'festival, boho, paisley, summer', tops: { color: '#ffffff', pattern: 'boho', scale: 5 }, shoes: { color: '#a0703f' }, thumbPattern: 'boho' },
  { id: 'a-tiedye', base: 'a', name: 'Tie-dye festival', vibe: 'festival', tags: 'festival, rainbow, tie dye, playful', tops: { color: '#ffffff', pattern: 'tiedye', scale: 3 }, shoes: { color: '#f4f4f2' }, thumbPattern: 'tiedye' },
  { id: 'a-neon', base: 'a', name: 'Neon circuit', vibe: 'cyberpunk', tags: 'cyberpunk, neon, techwear, night', tops: { color: '#ffffff', pattern: 'neon', scale: 4 }, shoes: { color: '#151517' }, thumbPattern: 'neon' },
  { id: 'a-mudcloth', base: 'a', name: 'Mudcloth-inspired', vibe: 'traditional-inspired', tags: 'mudcloth inspired, earthy brown, cream symbols', tops: { color: '#ffffff', pattern: 'mudcloth', scale: 5 }, shoes: { color: '#5a3b22' }, thumbPattern: 'mudcloth' },
  { id: 'a-batik', base: 'a', name: 'Batik-inspired', vibe: 'traditional-inspired', tags: 'batik inspired, brown rust cream', tops: { color: '#ffffff', pattern: 'batik', scale: 5 }, shoes: { color: '#6b4426' }, thumbPattern: 'batik' },
  { id: 'a-bloom', base: 'a', name: 'Ankara bloom', vibe: 'festival', tags: 'floral wax print inspired, magenta yellow', tops: { color: '#ffffff', pattern: 'ankara-bloom', scale: 5 }, shoes: { color: '#e0b020' }, thumbPattern: 'ankara-bloom' },
  { id: 'a-classic', base: 'a', name: 'Classic white', vibe: 'casual', tags: 'simple white dress, minimal', tops: { color: '#f3eee6' }, shoes: { color: '#e9e4dc' } },
  // ---- Body B: hoodie + trousers ----
  { id: 'b-street', base: 'b', name: 'Black + camo street', vibe: 'streetwear', tags: 'streetwear, black hoodie, camo cargo', tops: { color: '#232326', shading: 1.2 }, bottoms: { color: '#ffffff', pattern: 'camo', scale: 4 }, shoes: { color: '#f2f2f0' } },
  { id: 'b-kente', base: 'b', name: 'Kente-inspired hoodie', vibe: 'traditional-inspired', tags: 'kente inspired hoodie, gold green red, black trousers', tops: { color: '#ffffff', pattern: 'kente', scale: 5 }, bottoms: { color: '#1e1e21' }, shoes: { color: '#c9a227' }, thumbPattern: 'kente' },
  { id: 'b-afrofuture', base: 'b', name: 'Afrofuturist', vibe: 'afrofuturist', tags: 'afrofuturist, gold geometry on purple, sci-fi royal', tops: { color: '#ffffff', pattern: 'afrofuture', scale: 4 }, bottoms: { color: '#2c1b4a' }, shoes: { color: '#d4af37' }, thumbPattern: 'afrofuture' },
  { id: 'b-neon', base: 'b', name: 'Cyber neon', vibe: 'cyberpunk', tags: 'cyberpunk, neon circuit, techwear', tops: { color: '#ffffff', pattern: 'neon', scale: 4 }, bottoms: { color: '#141416' }, shoes: { color: '#141416' }, thumbPattern: 'neon' },
  { id: 'b-smart', base: 'b', name: 'Smart knit + pinstripe', vibe: 'business', tags: 'business casual, cream knit, pinstripe trousers, office', tops: { color: '#ffffff', pattern: 'knit', scale: 5 }, bottoms: { color: '#ffffff', pattern: 'pinstripe', scale: 6 }, shoes: { color: '#1a1a1c' }, thumbPattern: 'knit' },
  { id: 'b-navy', base: 'b', name: 'Navy smart casual', vibe: 'business', tags: 'navy, charcoal trousers, professional', tops: { color: '#26314f', shading: 1.1 }, bottoms: { color: '#3a3b40' }, shoes: { color: '#3b2516' } },
  { id: 'b-denim', base: 'b', name: 'Grey + denim', vibe: 'casual', tags: 'casual, heather grey hoodie, jeans', tops: { color: '#8d9096' }, bottoms: { color: '#ffffff', pattern: 'denim', scale: 5 }, shoes: { color: '#f2f2f0' }, thumbPattern: 'denim' },
  { id: 'b-tiedye', base: 'b', name: 'Tie-dye festival', vibe: 'festival', tags: 'festival, rainbow tie dye, jeans', tops: { color: '#ffffff', pattern: 'tiedye', scale: 3 }, bottoms: { color: '#ffffff', pattern: 'denim', scale: 5 }, shoes: { color: '#f2f2f0' }, thumbPattern: 'tiedye' },
  { id: 'b-camo', base: 'b', name: 'Olive camo', vibe: 'streetwear', tags: 'camo hoodie, olive cargo, utility', tops: { color: '#ffffff', pattern: 'camo', scale: 4 }, bottoms: { color: '#4b5130' }, shoes: { color: '#2a2a2a' }, thumbPattern: 'camo' },
  { id: 'b-mudcloth', base: 'b', name: 'Mudcloth-inspired', vibe: 'traditional-inspired', tags: 'mudcloth inspired, earthy, cream trousers', tops: { color: '#ffffff', pattern: 'mudcloth', scale: 5 }, bottoms: { color: '#e9dfcc' }, shoes: { color: '#5a3b22' }, thumbPattern: 'mudcloth' },
  { id: 'b-adire', base: 'b', name: 'Adire indigo', vibe: 'traditional-inspired', tags: 'indigo resist dye inspired, blue white', tops: { color: '#ffffff', pattern: 'adire', scale: 5 }, bottoms: { color: '#1f2a52' }, shoes: { color: '#efe6d6' }, thumbPattern: 'adire' },
  { id: 'b-bloom', base: 'b', name: 'Ankara bloom', vibe: 'festival', tags: 'floral wax print inspired hoodie, bright', tops: { color: '#ffffff', pattern: 'ankara-bloom', scale: 5 }, bottoms: { color: '#1e1e21' }, shoes: { color: '#e0b020' }, thumbPattern: 'ankara-bloom' },
  { id: 'b-varsity', base: 'b', name: 'Varsity red', vibe: 'streetwear', tags: 'red hoodie, grey joggers, sporty', tops: { color: '#b3261e' }, bottoms: { color: '#7d7f85' }, shoes: { color: '#f2f2f0' } },
  { id: 'b-lavender', base: 'b', name: 'Pastel lavender', vibe: 'casual', tags: 'pastel, soft, lavender hoodie, cream', tops: { color: '#b9a6e6' }, bottoms: { color: '#ece4d6' }, shoes: { color: '#f2f2f0' } },
];

/** v2 parts (extras.ts) are optional: selections / recipes without them build as before. */
export interface BuilderSelection extends Partial<BuilderExtras> {
  base: BaseId;
  skin: string;
  hair: string;
  /** A HAIR_COLORS id, or a FABRIC_COLORS id for head coverings. */
  hairColor: string;
  eyes: string;
  outfit: string;
}

export const DEFAULT_SELECTION: BuilderSelection = {
  base: 'a', skin: 'deep-brown', hair: 'afro', hairColor: 'soft-black', eyes: 'dark-brown', outfit: 'a-ankara-sun',
};

export const byId = <T extends { id: string }>(list: T[], id: string | undefined, fallback?: T): T => list.find((x) => x.id === id) ?? fallback ?? list[0];

/** Repair a selection so every id exists and the outfit/colour fit the base/style. */
export function normalizeSelection(s: Partial<BuilderSelection>): BuilderSelection {
  const base = s.base === 'b' ? 'b' : 'a';
  const hair = byId(HAIR_STYLES, s.hair, byId(HAIR_STYLES, DEFAULT_SELECTION.hair));
  const outfitOk = OUTFITS.find((o) => o.id === s.outfit && o.base === base);
  const outfit = outfitOk ?? OUTFITS.find((o) => o.base === base)!;
  const colorList = hair.fabric ? FABRIC_COLORS : HAIR_COLORS;
  const hairColor = colorList.find((c) => c.id === s.hairColor)?.id ?? colorList[0].id;
  return {
    base,
    skin: byId(SKIN_TONES, s.skin, byId(SKIN_TONES, DEFAULT_SELECTION.skin)).id,
    hair: hair.id,
    hairColor,
    eyes: byId(EYE_COLORS, s.eyes).id,
    outfit: outfit.id,
    ...normalizeExtras(s),
  };
}

/** Brow colour: follows the hair, or a natural dark brown under a covering / bald. */
export function browHex(sel: BuilderSelection): string {
  const hair = byId(HAIR_STYLES, sel.hair);
  if (hair.fabric || hair.id === 'bald') return '#2e2219';
  return byId(HAIR_COLORS, sel.hairColor).hex;
}

/** Colour + optional print for the hair material. */
export function hairMaterialSpec(sel: BuilderSelection): { hex: string; pattern?: string; scale?: number } {
  const hair = byId(HAIR_STYLES, sel.hair);
  if (hair.fabric) {
    const f = byId(FABRIC_COLORS, sel.hairColor);
    return { hex: f.hex, pattern: f.pattern, scale: f.scale };
  }
  return { hex: byId(HAIR_COLORS, sel.hairColor).hex };
}
